import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Video, VideoOff, UserCheck, Timer, Play, Pause, RotateCcw, Coins,
  LayoutGrid, Rocket, Check, ChevronRight, ChevronLeft, MapPin, CircleDot,
} from "lucide-react";

const STEPS = ["Challenge Court", "Check-In & Warm-Up", "Serving Bands", "Coin Toss", "Layout Check", "Launch"];
const TOSS_CHOICES = [
  { v: "serve", l: "Serve" }, { v: "receive", l: "Receive" },
  { v: "defer", l: "Defer" }, { v: "side", l: "Pick side" },
];

const playersOf = (name) => (name || "").split(/\s*\/\s*/).map((s) => s.trim()).filter(Boolean);

function StepDots({ step }) {
  return (
    <div className="flex items-center justify-center gap-1.5 mb-6" data-testid="wizard-progress">
      {STEPS.map((_, i) => (
        <div key={i} className="h-1.5 rounded-full transition-all" data-testid={`wizard-dot-${i}`}
          style={{ width: i === step ? 28 : 10, background: i <= step ? "var(--primary)" : "var(--border)" }} />
      ))}
    </div>
  );
}

function fmt(s) {
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

export default function PreMatchWizard({ aName, bName, courtName, onLaunch, launching }) {
  const [step, setStep] = useState(0);
  const [challengeCourt, setChallengeCourt] = useState(false);
  const [checked, setChecked] = useState({}); // key `${team}-${i}` -> bool
  const [warmSecs, setWarmSecs] = useState(300);
  const [warmRunning, setWarmRunning] = useState(false);
  const [bandA, setBandA] = useState(null); // player index on team A wearing the band
  const [bandB, setBandB] = useState(null); // player index on team B wearing the band
  const [tossWinner, setTossWinner] = useState(null); // 'a' | 'b'
  const [tossChoice, setTossChoice] = useState(null);
  const [layoutOk, setLayoutOk] = useState(false);
  const timerRef = useRef(null);

  const aPlayers = playersOf(aName);
  const bPlayers = playersOf(bName);
  const allPlayers = [
    ...aPlayers.map((name, i) => ({ team: "a", i, name })),
    ...bPlayers.map((name, i) => ({ team: "b", i, name })),
  ];
  const key = (p) => `${p.team}-${p.i}`;

  const needBandA = aPlayers.length > 1;
  const needBandB = bPlayers.length > 1;
  const bandNameA = needBandA ? (bandA != null ? aPlayers[bandA] : null) : (aPlayers[0] || null);
  const bandNameB = needBandB ? (bandB != null ? bPlayers[bandB] : null) : (bPlayers[0] || null);
  // first server is decided by the coin toss: winner either serves, or the opponent serves
  const firstServer = tossWinner ? (tossChoice === "serve" ? tossWinner : (tossWinner === "a" ? "b" : "a")) : null;

  useEffect(() => {
    if (!warmRunning) return;
    timerRef.current = setInterval(() => setWarmSecs((s) => (s <= 1 ? (clearInterval(timerRef.current), setWarmRunning(false), 0) : s - 1)), 1000);
    return () => clearInterval(timerRef.current);
  }, [warmRunning]);

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const allChecked = allPlayers.length > 0 && allPlayers.every((p) => checked[key(p)]);
  const bandsChosen = (!needBandA || bandA != null) && (!needBandB || bandB != null);
  const canNext = () => {
    if (step === 1) return allChecked;
    if (step === 2) return bandsChosen;
    if (step === 3) return !!tossWinner && !!tossChoice;
    if (step === 4) return layoutOk;
    return true;
  };

  const doLaunch = () => onLaunch({
    first_server_team: firstServer || "a",
    setup: {
      challenge_court: challengeCourt,
      checked_in_players: allPlayers.filter((p) => checked[key(p)]).map((p) => p.name),
      warmup_used_seconds: 300 - warmSecs,
      serving_bands: { a: bandNameA, b: bandNameB },
      serving_band_player: firstServer === "a" ? bandNameA : bandNameB,
      serving_bands_team: firstServer,
      coin_toss: { winner: tossWinner, choice: tossChoice },
      layout_verified: layoutOk,
    },
  });

  const teamCard = (side, name, active, onClick, testid) => (
    <button data-testid={testid || `wizard-team-${side}`} onClick={onClick}
      className="flex-1 rounded-2xl glow-border p-4 text-center transition-transform hover:scale-[1.01]"
      style={{ background: active ? "rgba(0,229,255,0.12)" : "var(--surface)", borderColor: active ? "var(--primary)" : "var(--border)" }}>
      {active && <Check className="w-5 h-5 mx-auto mb-1 text-primary-c" />}
      <div className="font-bold text-sm truncate">{name || "—"}</div>
    </button>
  );

  const CheckInColumn = ({ side, label, players }) => (
    <div className="flex-1">
      <div className="text-[10px] mono uppercase tracking-wider mb-2" style={{ color: "var(--text-2)" }}>{label}</div>
      <div className="space-y-2">
        {players.length === 0 && <div className="text-xs" style={{ color: "var(--text-2)" }}>—</div>}
        {players.map((p) => {
          const k = key(p);
          const on = !!checked[k];
          return (
            <button key={k} data-testid={`checkin-${side}-${p.i}`} onClick={() => setChecked((c) => ({ ...c, [k]: !c[k] }))}
              className="w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left transition-colors"
              style={{ background: on ? "rgba(34,197,94,0.14)" : "var(--surface)", border: `1px solid ${on ? "#22c55e" : "var(--border)"}` }}>
              <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: on ? "#22c55e" : "var(--bg)", border: on ? "none" : "1px solid var(--border)" }}>
                {on ? <Check className="w-3.5 h-3.5 text-black" /> : <span className="w-2 h-2 rounded-full" style={{ background: "var(--text-2)" }} />}
              </span>
              <span className="font-bold text-sm truncate">{p.name}</span>
              <span className="ml-auto text-[10px] mono uppercase tracking-wider" style={{ color: on ? "#22c55e" : "var(--text-2)" }}>{on ? "In" : "Tap"}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const BandTeam = ({ side, label, players, value, onSelect, single }) => (
    <div className="flex-1">
      <div className="text-[10px] mono uppercase tracking-wider mb-2" style={{ color: "var(--text-2)" }}>{label}</div>
      <div className="space-y-2">
        {players.map((name, idx) => {
          const selected = single || value === idx;
          return (
            <button key={idx} data-testid={`band-${side}-${idx}`} onClick={() => !single && onSelect(idx)} disabled={single}
              className="w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left transition-colors"
              style={{ background: selected ? "rgba(0,229,255,0.14)" : "var(--surface)", border: `1px solid ${selected ? "var(--primary)" : "var(--border)"}` }}>
              <CircleDot className="w-4 h-4 shrink-0" style={{ color: selected ? "var(--primary)" : "var(--border)" }} />
              <span className="font-bold text-sm truncate">{name}</span>
              <span className="ml-auto text-[10px] mono uppercase tracking-wider" style={{ color: selected ? "var(--primary)" : "var(--text-2)" }}>{selected ? "Has band" : "No band"}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div data-testid="prematch-wizard">
      {courtName && (
        <div className="flex items-center justify-center gap-1.5 mb-3 text-xs mono uppercase tracking-wider text-primary-c">
          <MapPin className="w-3.5 h-3.5" /> {courtName}
        </div>
      )}
      <h1 className="font-display font-bold text-2xl text-center mb-1">Pre-Match Setup</h1>
      <p className="text-center text-xs mb-5" style={{ color: "var(--text-2)" }}>Step {step + 1} of {STEPS.length} · {STEPS[step]}</p>
      <StepDots step={step} />

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}
          className="rounded-3xl glow-border p-6 min-h-[280px] flex flex-col" style={{ background: "var(--surface)" }}>

          {step === 0 && (
            <div data-testid="step-challenge" className="flex-1 flex flex-col items-center justify-center text-center gap-5">
              <div className="w-16 h-16 rounded-2xl glass flex items-center justify-center">
                {challengeCourt ? <Video className="w-7 h-7 text-primary-c" /> : <VideoOff className="w-7 h-7" style={{ color: "var(--text-2)" }} />}
              </div>
              <div>
                <div className="font-display font-bold text-lg">Challenge Court</div>
                <p className="text-sm mt-1 max-w-xs" style={{ color: "var(--text-2)" }}>Enable if this court has video review for line-call challenges.</p>
              </div>
              <button data-testid="toggle-challenge" onClick={() => setChallengeCourt((v) => !v)}
                className="relative w-20 h-10 rounded-full transition-colors" style={{ background: challengeCourt ? "var(--primary)" : "var(--border)" }}>
                <span className="absolute top-1 left-1 w-8 h-8 rounded-full bg-white transition-transform" style={{ transform: challengeCourt ? "translateX(40px)" : "none" }} />
              </button>
              <span className="text-xs mono uppercase tracking-wider" style={{ color: challengeCourt ? "var(--primary)" : "var(--text-2)" }}>{challengeCourt ? "Challenge court ON" : "Standard court"}</span>
            </div>
          )}

          {step === 1 && (
            <div data-testid="step-checkin" className="flex-1 flex flex-col gap-4">
              <div className="flex items-center gap-2 text-sm font-bold"><UserCheck className="w-4 h-4 text-primary-c" /> Tap each player to check them in</div>
              <div className="flex gap-4">
                <CheckInColumn side="a" label={needBandA ? "Team A" : "Player A"} players={allPlayers.filter((p) => p.team === "a")} />
                <div className="w-px" style={{ background: "var(--border)" }} />
                <CheckInColumn side="b" label={needBandB ? "Team B" : "Player B"} players={allPlayers.filter((p) => p.team === "b")} />
              </div>
              <div className="rounded-2xl glass p-4 mt-1">
                <div className="flex items-center gap-2 text-xs mono uppercase tracking-wider mb-2" style={{ color: "var(--text-2)" }}><Timer className="w-3.5 h-3.5 text-primary-c" /> Warm-up timer</div>
                <div className="flex items-center gap-4">
                  <div className="font-display font-black text-4xl tracking-tighter" data-testid="warmup-clock">{fmt(warmSecs)}</div>
                  <div className="flex gap-2">
                    <button data-testid="warmup-toggle" onClick={() => setWarmRunning((v) => !v)} className="w-10 h-10 rounded-full glass flex items-center justify-center">
                      {warmRunning ? <Pause className="w-4 h-4 text-primary-c" /> : <Play className="w-4 h-4 text-primary-c" />}
                    </button>
                    <button data-testid="warmup-reset" onClick={() => { setWarmRunning(false); setWarmSecs(300); }} className="w-10 h-10 rounded-full glass flex items-center justify-center">
                      <RotateCcw className="w-4 h-4" style={{ color: "var(--text-2)" }} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-serving-bands" className="flex-1 flex flex-col gap-4">
              <div className="flex items-center gap-2 text-sm font-bold"><CircleDot className="w-4 h-4 text-primary-c" /> Serving bands</div>
              <p className="text-xs" style={{ color: "var(--text-2)" }}>{needBandA || needBandB ? "Each team taps the player wearing the serving band (their starting server)." : "Confirm each side's server."}</p>
              <div className="flex gap-4">
                <BandTeam side="a" label={needBandA ? "Team A" : "Player A"} players={aPlayers} value={bandA} onSelect={setBandA} single={!needBandA} />
                <div className="w-px" style={{ background: "var(--border)" }} />
                <BandTeam side="b" label={needBandB ? "Team B" : "Player B"} players={bPlayers} value={bandB} onSelect={setBandB} single={!needBandB} />
              </div>
              {bandsChosen && (
                <div className="rounded-xl glass px-4 py-2.5 text-xs" data-testid="band-summary" style={{ color: "var(--text-2)" }}>
                  <CircleDot className="w-3.5 h-3.5 text-primary-c inline mr-1" /> Bands set. The coin toss (next step) decides who serves first.
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-coin-toss" className="flex-1 flex flex-col gap-4">
              <div className="flex items-center gap-2 text-sm font-bold"><Coins className="w-4 h-4 text-primary-c" /> Coin toss</div>
              <div>
                <div className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: "var(--text-2)" }}>Toss winner</div>
                <div className="flex gap-3">
                  {teamCard("a", aName, tossWinner === "a", () => setTossWinner("a"))}
                  {teamCard("b", bName, tossWinner === "b", () => setTossWinner("b"))}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: "var(--text-2)" }}>Their choice</div>
                <div className="grid grid-cols-4 gap-2">
                  {TOSS_CHOICES.map((c) => (
                    <button key={c.v} data-testid={`toss-choice-${c.v}`} onClick={() => setTossChoice(c.v)}
                      className="rounded-xl py-2.5 text-xs font-bold transition-colors"
                      style={tossChoice === c.v ? { background: "var(--primary)", color: "var(--on-primary)" } : { background: "var(--bg)", color: "var(--text-2)", border: "1px solid var(--border)" }}>
                      {c.l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="step-layout" className="flex-1 flex flex-col gap-3">
              <div className="flex items-center gap-2 text-sm font-bold"><LayoutGrid className="w-4 h-4 text-primary-c" /> Layout verification</div>
              <div className="rounded-2xl glass p-4 space-y-2 text-sm">
                <SummaryRow label="Court" value={courtName || "—"} />
                <SummaryRow label="Type" value={challengeCourt ? "Challenge (video)" : "Standard"} />
                <SummaryRow label="Checked in" value={`${allPlayers.filter((p) => checked[key(p)]).length}/${allPlayers.length}`} />
                <SummaryRow label="Band · A" value={bandNameA || "—"} icon />
                <SummaryRow label="Band · B" value={bandNameB || "—"} icon />
                <SummaryRow label="Serves first" value={firstServer === "a" ? aName : firstServer === "b" ? bName : "—"} />
                <SummaryRow label="Toss" value={tossWinner ? `${tossWinner === "a" ? aName : bName} · ${TOSS_CHOICES.find((c) => c.v === tossChoice)?.l || "—"}` : "—"} />
              </div>
              <label className="flex items-center gap-2.5 mt-1 cursor-pointer rounded-xl glass p-3" data-testid="layout-confirm">
                <input type="checkbox" checked={layoutOk} onChange={(e) => setLayoutOk(e.target.checked)} className="w-5 h-5 accent-lime-400" />
                <span className="text-sm">Digital board matches the physical court alignment.</span>
              </label>
            </div>
          )}

          {step === 5 && (
            <div data-testid="step-launch" className="flex-1 flex flex-col items-center justify-center text-center gap-4">
              <div className="w-16 h-16 rounded-2xl glass flex items-center justify-center"><Rocket className="w-7 h-7 text-primary-c" /></div>
              <div className="font-display font-bold text-lg">Ready to score</div>
              <p className="text-sm max-w-xs flex items-center gap-1.5 justify-center flex-wrap" style={{ color: "var(--text-2)" }}>
                <CircleDot className="w-4 h-4 text-primary-c" /> <span className="font-bold" style={{ color: "var(--text)" }}>{firstServer === "a" ? bandNameA : bandNameB}</span> serves first with the band.
              </p>
              <button data-testid="launch-scoring" onClick={doLaunch} disabled={launching}
                className="btn-glow rounded-2xl px-8 py-4 font-black tracking-wider flex items-center gap-2 disabled:opacity-60">
                <Play className="w-5 h-5" /> {launching ? "Starting…" : "TAP TO START LIVE SCORING"}
              </button>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center justify-between mt-5">
        <button data-testid="wizard-back" onClick={back} disabled={step === 0}
          className="glass rounded-full px-4 py-2 text-sm font-bold flex items-center gap-1.5 disabled:opacity-30" style={{ color: "var(--text)" }}>
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        {step < STEPS.length - 1 && (
          <button data-testid="wizard-next" onClick={next} disabled={!canNext()}
            className="btn-glow rounded-full px-5 py-2 text-sm font-bold flex items-center gap-1.5 disabled:opacity-40">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function SummaryRow({ label, value, icon }) {
  return (
    <div className="flex items-center justify-between">
      <span style={{ color: "var(--text-2)" }}>{label}</span>
      <span className="font-bold flex items-center gap-1.5">{icon && value !== "—" && <CircleDot className="w-3.5 h-3.5 text-primary-c" />}{value}</span>
    </div>
  );
}
